import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, AlertTriangle } from 'lucide-react';
import AdminLayout from '../components/admin/AdminLayout';

const API = process.env.REACT_APP_BACKEND_URL;

/* ─────────────────────── Login ─────────────────────── */

function LoginScreen({ onLogin, error }) {
  const [pw, setPw] = useState('');
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  return (
    <div style={ls.wrap}>
      <div style={ls.card}>
        <div style={{ fontSize: 28, marginBottom: 8 }}>🌲</div>
        <h1 style={ls.title}>Magic Forest</h1>
        <p style={ls.sub}>Health Monitor</p>
        <form onSubmit={e => { e.preventDefault(); onLogin(pw); }} style={{ width: '100%', marginTop: 24 }}>
          <input ref={inputRef} type="password" placeholder="Admin password"
            value={pw} onChange={e => setPw(e.target.value)} style={ls.input} />
          {error && <p style={ls.error}>{error}</p>}
          <button type="submit" style={ls.btn}>Sign In</button>
        </form>
      </div>
    </div>
  );
}

/* ─────────────────────── Helpers ─────────────────────── */

const ago = (iso) => {
  if (!iso) return '—';
  const d = (Date.now() - new Date(iso)) / 1000;
  if (d < 60) return `${Math.floor(d)}s ago`;
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
};

const MODE = {
  worker_primary: {
    color: '#4ade80', label: 'Worker Primary', desc: 'Workers handle rendering',
    warning: null,
  },
  render_emergency: {
    color: '#f87171', label: 'Emergency', desc: 'Backend renders locally',
    warning: 'Worker rendering will be DISABLED. All image and video exports will use local Playwright on Render.com — significantly slower and resource-limited. Only use this if workers are completely unreachable.',
  },
  worker_required: {
    color: '#60a5fa', label: 'Worker Required', desc: 'Workers only, no fallback',
    warning: 'Local fallback will be DISABLED. If workers go down, ALL rendering will fail with errors. Only use this for testing worker reliability.',
  },
};

/* ─────────────────────── Main ─────────────────────── */

export default function AdminHealth() {
  const [password, setPassword] = useState(() => sessionStorage.getItem('admin_pw') || '');
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState('');
  const [loading, setLoading] = useState(false);
  const [health, setHealth] = useState(null);
  const [checks, setChecks] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [history, setHistory] = useState([]);
  const [showSwitch, setShowSwitch] = useState(false);
  const [switchMode, setSwitchMode] = useState('');
  const [switchReason, setSwitchReason] = useState('');
  const [switchConfirmed, setSwitchConfirmed] = useState(false);
  const [switching, setSwitching] = useState(false);
  const intervalRef = useRef(null);
  const headers = { 'X-Admin-Password': password };

  const handleLogin = async (pw) => {
    setAuthError('');
    try {
      const resp = await fetch(`${API}/api/admin/auth`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pw }),
      });
      if (resp.status === 401) { setAuthError('Invalid password'); return; }
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setPassword(pw); sessionStorage.setItem('admin_pw', pw); setAuthed(true);
    } catch { setAuthError('Connection failed'); }
  };

  useEffect(() => { if (password && !authed) handleLogin(password); }, []);

  const fetchAll = useCallback(async () => {
    if (!password) return;
    setLoading(true);
    try {
      const [st, ch, inc, hist] = await Promise.all([
        fetch(`${API}/api/admin/health/status`, { headers }).then(r => r.json()),
        fetch(`${API}/api/admin/health/checks?limit=60`, { headers }).then(r => r.json()),
        fetch(`${API}/api/admin/health/incidents?include_resolved=true&limit=20`, { headers }).then(r => r.json()),
        fetch(`${API}/api/admin/health/execution-mode/history?limit=10`, { headers }).then(r => r.json()),
      ]);
      setHealth(st); setChecks(ch.checks || []); setIncidents(inc.incidents || []); setHistory(hist.history || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [password]);

  useEffect(() => { if (authed) fetchAll(); }, [authed, fetchAll]);
  useEffect(() => {
    if (!authed) return;
    intervalRef.current = setInterval(fetchAll, 30000);
    return () => clearInterval(intervalRef.current);
  }, [authed, fetchAll]);

  const doSwitch = async () => {
    if (!switchMode || !switchReason) return;
    // Require explicit confirmation for dangerous modes
    if (!switchConfirmed && MODE[switchMode]?.warning) {
      return;
    }
    setSwitching(true);
    try {
      await fetch(`${API}/api/admin/health/execution-mode`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode: switchMode, reason: switchReason }),
      });
      setShowSwitch(false); setSwitchMode(''); setSwitchReason(''); setSwitchConfirmed(false);
      fetchAll();
    } catch (e) { console.error(e); }
    setSwitching(false);
  };

  const resolveInc = async (id) => {
    await fetch(`${API}/api/admin/health/incidents/${id}/resolve`, { method: 'POST', headers });
    fetchAll();
  };

  if (!authed) return <LoginScreen onLogin={handleLogin} error={authError} />;

  const mode = health?.execution_mode;
  const mc = MODE[mode?.mode] || MODE.worker_primary;
  const ok = health?.overall_status === 'healthy';

  return (
    <AdminLayout activePage="health" onLogout={() => { sessionStorage.removeItem('admin_pw'); setAuthed(false); }}>
      {/* Top */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <p style={{ margin: 0, fontSize: 13, color: '#525252' }}>Workers, execution mode & incidents</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={fetchAll} disabled={loading} style={p.refreshBtn}>
            <RefreshCw size={13} /> {loading ? 'Checking...' : 'Refresh'}
          </button>
          <span style={{ fontSize: 10, color: '#2a2a2a', fontFamily: 'monospace' }}>auto: 30s</span>
        </div>
      </div>

      {/* Health + Mode cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 28 }}>
        {/* Health */}
        <div style={{ ...p.card, borderColor: ok ? '#1a3a1a' : '#3a2a1a' }}>
          <div style={{ fontSize: 10, color: '#3a3a3a', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>System Health</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 10, height: 10, borderRadius: '50%',
              backgroundColor: ok ? '#4ade80' : '#fbbf24',
              boxShadow: `0 0 8px ${ok ? '#4ade80' : '#fbbf24'}40`,
            }} />
            <span style={{ fontSize: 20, fontWeight: 700, color: ok ? '#4ade80' : '#fbbf24' }}>
              {ok ? 'Healthy' : health?.overall_status || '—'}
            </span>
          </div>
          {health?.open_incidents?.length > 0 && (
            <div style={{ marginTop: 8, fontSize: 11, color: '#fbbf24' }}>
              {health.open_incidents.length} open incident(s)
            </div>
          )}
        </div>

        {/* Mode */}
        <div style={p.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 10, color: '#3a3a3a', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Execution Mode</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: mc.color }}>{mc.label}</div>
              <div style={{ fontSize: 11, color: '#2a2a2a', marginTop: 2 }}>{mc.desc}</div>
            </div>
            <button onClick={() => setShowSwitch(true)} style={p.switchBtn}>Switch</button>
          </div>
          {mode?.changed_at && (
            <div style={{ marginTop: 8, fontSize: 10, color: '#2a2a2a' }}>
              by {mode.changed_by} · {ago(mode.changed_at)} · source: {mode.source}
            </div>
          )}
        </div>
      </div>

      {/* Workers */}
      <h2 style={p.sectionTitle}>Worker Fleet</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}>
        {health?.workers?.map((w, i) => {
          const wOk = w.status === true;
          const name = w.url?.replace(/^https?:\/\//, '').split('.')[0] || 'unknown';
          const wc = (checks || []).filter(c => c.worker_url === w.url).slice(0, 30);
          const total = wc.length;
          const up = wc.filter(c => c.is_healthy).length;
          const pct = total > 0 ? ((up / total) * 100).toFixed(1) : '—';

          return (
            <div key={i} style={{ ...p.card, borderColor: wOk ? '#1a2a1a' : '#2a1a1a' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#e5e5e5' }}>🖥️ {name}</span>
                    <span style={{
                      padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                      backgroundColor: wOk ? '#0a1a0a' : '#1a0a0a',
                      color: wOk ? '#4ade80' : '#f87171',
                      border: `1px solid ${wOk ? '#1a3a1a' : '#3a1a1a'}`,
                    }}>
                      {wOk ? 'Online' : 'Down'}
                    </span>
                  </div>
                  <div style={{ fontSize: 10, color: '#2a2a2a', fontFamily: 'monospace' }}>{w.url}</div>
                </div>
                <div style={{ display: 'flex', gap: 20 }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 17, fontWeight: 700, color: '#4ade80', fontFamily: 'monospace' }}>{pct}{pct !== '—' && '%'}</div>
                    <div style={{ fontSize: 9, color: '#2a2a2a' }}>Uptime</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 17, fontWeight: 700, color: '#e5e5e5', fontFamily: 'monospace' }}>
                      {w.response_time_ms ?? '—'}<span style={{ fontSize: 10, color: '#3a3a3a' }}>ms</span>
                    </div>
                    <div style={{ fontSize: 9, color: '#2a2a2a' }}>Latency</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 17, fontWeight: 700, color: '#e5e5e5', fontFamily: 'monospace' }}>{w.queue_size ?? '—'}</div>
                    <div style={{ fontSize: 9, color: '#2a2a2a' }}>Queue</div>
                  </div>
                </div>
              </div>
              {wc.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ display: 'flex', gap: 2 }}>
                    {[...wc].reverse().map((c, j) => (
                      <div key={j} style={{ flex: 1, height: 18, borderRadius: 2, minWidth: 4, backgroundColor: c.is_healthy ? '#0f3d0f' : '#3d0f0f' }}
                        title={`${c.is_healthy ? 'OK' : c.error_message || 'FAIL'}`} />
                    ))}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3 }}>
                    <span style={{ fontSize: 8, color: '#2a2a2a' }}>older</span>
                    <span style={{ fontSize: 8, color: '#2a2a2a' }}>now</span>
                  </div>
                </div>
              )}
              {w.error && (
                <div style={{ marginTop: 8, padding: '5px 8px', borderRadius: 4, backgroundColor: '#140a0a', fontSize: 11, color: '#f87171', fontFamily: 'monospace' }}>{w.error}</div>
              )}
            </div>
          );
        })}
        {(!health?.workers || health.workers.length === 0) && <div style={p.empty}>No workers configured</div>}
      </div>

      {/* Incidents */}
      <h2 style={p.sectionTitle}>Incidents</h2>
      {incidents.length === 0 ? (
        <div style={p.emptyCard}>No incidents recorded</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 28 }}>
          {incidents.map(inc => {
            const open = !inc.resolved_at;
            const sevC = inc.severity === 'critical' ? '#f87171' : '#fbbf24';
            return (
              <div key={inc.id} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '9px 14px',
                borderRadius: 6, backgroundColor: '#0f0f0f', border: `1px solid ${open ? `${sevC}15` : '#1a1a1a'}`,
                opacity: open ? 1 : 0.45,
              }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: open ? sevC : '#3a3a3a' }} />
                <span style={{ fontSize: 11, fontWeight: 600, color: open ? '#e5e5e5' : '#3a3a3a', textTransform: 'uppercase', minWidth: 60 }}>{inc.severity}</span>
                <span style={{ fontSize: 11, color: '#3a3a3a', flex: 1 }}>{inc.worker_url?.replace(/^https?:\/\//, '').split('.')[0]}</span>
                <span style={{ fontSize: 10, color: '#2a2a2a', fontFamily: 'monospace' }}>{ago(inc.opened_at)}</span>
                {open ? (
                  <button onClick={() => resolveInc(inc.id)} style={{ padding: '3px 9px', borderRadius: 4, border: '1px solid #1a3a1a', backgroundColor: '#0a140a', color: '#4ade80', cursor: 'pointer', fontSize: 10, fontWeight: 600, fontFamily: 'Inter, system-ui, sans-serif' }}>Resolve</button>
                ) : (
                  <span style={{ fontSize: 10, color: '#2d5a2d' }}>✓</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Mode History */}
      <h2 style={p.sectionTitle}>Mode Change Audit</h2>
      {history.length === 0 ? (
        <div style={p.emptyCard}>No mode changes recorded</div>
      ) : (
        <div style={{ ...p.tableWrap, marginBottom: 28 }}>
          <table style={p.table}>
            <thead>
              <tr>
                <th style={p.th}>When</th><th style={p.th}>From</th><th style={p.th}>To</th><th style={p.th}>By</th><th style={p.th}>Reason</th>
              </tr>
            </thead>
            <tbody>
              {history.map(h => (
                <tr key={h.id}>
                  <td style={{ ...p.td, fontFamily: 'monospace', fontSize: 11 }}>{ago(h.changed_at)}</td>
                  <td style={p.td}>{h.previous_mode ? <MBadge m={h.previous_mode} /> : '—'}</td>
                  <td style={p.td}><MBadge m={h.mode} /></td>
                  <td style={p.td}>{h.changed_by}</td>
                  <td style={{ ...p.td, color: '#3a3a3a', fontSize: 11 }}>{h.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Recent Checks */}
      <h2 style={p.sectionTitle}>Recent Health Checks</h2>
      <div style={p.tableWrap}>
        <div style={{ maxHeight: 260, overflow: 'auto' }}>
          <table style={p.table}>
            <thead>
              <tr>
                <th style={p.th}>Time</th><th style={p.th}>Worker</th><th style={p.th}>Status</th>
                <th style={p.th}>Latency</th><th style={p.th}>Queue</th><th style={p.th}>Error</th>
              </tr>
            </thead>
            <tbody>
              {checks.slice(0, 30).map(c => (
                <tr key={c.id}>
                  <td style={{ ...p.td, fontFamily: 'monospace', fontSize: 11 }}>{ago(c.checked_at)}</td>
                  <td style={{ ...p.td, fontSize: 11 }}>{c.worker_url?.replace(/^https?:\/\//, '').split('.')[0]}</td>
                  <td style={p.td}>
                    <span style={{ color: c.is_healthy ? '#4ade80' : '#f87171', fontSize: 11 }}>● {c.is_healthy ? 'OK' : 'FAIL'}</span>
                  </td>
                  <td style={{ ...p.td, fontFamily: 'monospace' }}>{c.response_time_ms != null ? `${c.response_time_ms}ms` : '—'}</td>
                  <td style={p.td}>{c.queue_size ?? '—'}</td>
                  <td style={{ ...p.td, fontSize: 10, color: '#f87171', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.error_message || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Switch Modal */}
      {showSwitch && (
        <div style={p.overlay}>
          <div style={p.modal}>
            <h3 style={{ margin: '0 0 16px', fontSize: 15, color: '#fafafa' }}>Switch Execution Mode</h3>
            
            {/* Current mode indicator */}
            <div style={{ marginBottom: 16, padding: '8px 12px', borderRadius: 6, backgroundColor: '#0a0a0a', border: '1px solid #1a1a1a' }}>
              <span style={{ fontSize: 10, color: '#3a3a3a' }}>Current: </span>
              <MBadge m={mode?.mode || 'worker_primary'} />
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 10, color: '#3a3a3a', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Select new mode</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {Object.entries(MODE).map(([k, v]) => (
                  <button key={k} onClick={() => { setSwitchMode(k); setSwitchConfirmed(false); }} style={{
                    flex: 1, padding: '10px 6px', borderRadius: 6, cursor: 'pointer', textAlign: 'center',
                    border: `1px solid ${switchMode === k ? v.color + '40' : '#1f1f1f'}`,
                    backgroundColor: switchMode === k ? v.color + '10' : '#0f0f0f',
                    color: switchMode === k ? v.color : '#3a3a3a',
                    fontSize: 11, fontWeight: 600, fontFamily: 'Inter, system-ui, sans-serif',
                  }}>
                    {v.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Impact warning for dangerous modes */}
            {switchMode && MODE[switchMode]?.warning && (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: '10px 14px', borderRadius: 6, marginBottom: 16,
                backgroundColor: switchMode === 'render_emergency' ? '#140a0a' : '#0a0d14',
                border: `1px solid ${switchMode === 'render_emergency' ? '#3a1a1a' : '#1a2a3a'}`,
                fontSize: 12, color: switchMode === 'render_emergency' ? '#f87171' : '#60a5fa',
                lineHeight: 1.4,
              }}>
                <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 3 }}>⚠ Impact Warning</div>
                  <div>{MODE[switchMode].warning}</div>
                </div>
              </div>
            )}

            {/* Note: display-only warning */}
            <div style={{
              padding: '8px 12px', borderRadius: 6, marginBottom: 16,
              backgroundColor: '#0a0a0a', border: '1px solid #1a1a1a',
              fontSize: 10, color: '#525252', lineHeight: 1.4,
            }}>
              <strong>Note:</strong> This switch updates the admin dashboard display. Actual rendering behavior
              is controlled by the <code style={{ color: '#737373' }}>MEDIA_EXECUTION_GLOBAL_MODE</code> environment
              variable on the server. To change rendering behavior, update the env var and restart.
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 10, color: '#3a3a3a', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Reason (required)</div>
              <input value={switchReason} onChange={e => setSwitchReason(e.target.value)}
                placeholder="Why are you switching?" style={p.modalInput} />
            </div>

            {/* Confirmation checkbox for dangerous modes */}
            {switchMode && MODE[switchMode]?.warning && (
              <label style={{
                display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16,
                fontSize: 12, color: '#737373', cursor: 'pointer',
              }}>
                <input
                  type="checkbox"
                  checked={switchConfirmed}
                  onChange={e => setSwitchConfirmed(e.target.checked)}
                  style={{ accentColor: '#f87171' }}
                />
                I understand the impact and want to proceed
              </label>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowSwitch(false); setSwitchMode(''); setSwitchReason(''); setSwitchConfirmed(false); }} style={p.cancelBtn}>Cancel</button>
              <button onClick={doSwitch}
                disabled={!switchMode || !switchReason || switching || (MODE[switchMode]?.warning && !switchConfirmed)}
                style={{
                  ...p.confirmBtn, 
                  opacity: (!switchMode || !switchReason || switching || (MODE[switchMode]?.warning && !switchConfirmed)) ? 0.35 : 1,
                }}>
                {switching ? '⏳' : '✓'} Confirm Switch
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function MBadge({ m }) {
  const c = MODE[m]?.color || '#525252';
  return (
    <span style={{
      display: 'inline-block', padding: '2px 7px', borderRadius: 4, fontSize: 10,
      fontWeight: 600, fontFamily: 'monospace',
      backgroundColor: `${c}10`, color: c, border: `1px solid ${c}20`,
    }}>{m}</span>
  );
}

const p = {
  refreshBtn: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '7px 14px', borderRadius: 6, border: '1px solid #1f1f1f',
    backgroundColor: '#141414', color: '#737373', cursor: 'pointer', fontSize: 12,
    fontFamily: 'Inter, system-ui, sans-serif',
  },
  card: { padding: 18, borderRadius: 8, backgroundColor: '#0f0f0f', border: '1px solid #1a1a1a' },
  switchBtn: {
    padding: '5px 12px', borderRadius: 6, border: '1px solid #1f1f1f',
    backgroundColor: '#141414', color: '#525252', cursor: 'pointer', fontSize: 11,
    fontFamily: 'Inter, system-ui, sans-serif',
  },
  sectionTitle: {
    fontSize: 12, fontWeight: 600, color: '#525252', textTransform: 'uppercase',
    letterSpacing: '0.06em', marginBottom: 12, marginTop: 0,
  },
  empty: { padding: 24, textAlign: 'center', color: '#2a2a2a', fontSize: 13 },
  emptyCard: {
    padding: 24, textAlign: 'center', color: '#2a2a2a', fontSize: 13,
    borderRadius: 8, backgroundColor: '#0f0f0f', border: '1px solid #1a1a1a', marginBottom: 28,
  },
  tableWrap: { borderRadius: 8, overflow: 'hidden', backgroundColor: '#0f0f0f', border: '1px solid #1a1a1a' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    padding: '9px 14px', textAlign: 'left', fontSize: 10, fontWeight: 600,
    color: '#2a2a2a', textTransform: 'uppercase', letterSpacing: '0.05em',
    borderBottom: '1px solid #1a1a1a',
  },
  td: { padding: '7px 14px', fontSize: 12, color: '#d4d4d4', borderBottom: '1px solid #111' },
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { padding: 24, borderRadius: 10, width: 400, backgroundColor: '#141414', border: '1px solid #1f1f1f' },
  modalInput: {
    width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #1f1f1f',
    backgroundColor: '#0a0a0a', color: '#e5e5e5', fontSize: 12, outline: 'none', boxSizing: 'border-box',
    fontFamily: 'Inter, system-ui, sans-serif',
  },
  cancelBtn: {
    padding: '7px 14px', borderRadius: 6, border: '1px solid #1f1f1f',
    backgroundColor: 'transparent', color: '#3a3a3a', cursor: 'pointer', fontSize: 12,
    fontFamily: 'Inter, system-ui, sans-serif',
  },
  confirmBtn: {
    padding: '7px 14px', borderRadius: 6, border: '1px solid #1a3a1a',
    backgroundColor: '#0a1a0a', color: '#4ade80', cursor: 'pointer', fontSize: 12, fontWeight: 600,
    fontFamily: 'Inter, system-ui, sans-serif',
  },
};

const ls = {
  wrap: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0a0a0a' },
  card: { padding: 40, borderRadius: 12, width: 340, textAlign: 'center', backgroundColor: '#0f0f0f', border: '1px solid #1a1a1a' },
  title: { margin: 0, fontSize: 20, fontWeight: 700, color: '#fafafa' },
  sub: { margin: '4px 0 0', fontSize: 12, color: '#525252' },
  input: { width: '100%', padding: '11px 14px', borderRadius: 6, border: '1px solid #1f1f1f', backgroundColor: '#0a0a0a', color: '#e5e5e5', fontSize: 13, outline: 'none', boxSizing: 'border-box', marginBottom: 12, fontFamily: 'Inter, system-ui, sans-serif' },
  btn: { width: '100%', padding: '11px 16px', borderRadius: 6, border: 'none', backgroundColor: '#4ade80', color: '#0a0a0a', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  error: { color: '#f87171', fontSize: 12, margin: '8px 0' },
};
